/**
 * @file useCanvasCart.ts
 * @description Корзина на канве конструктора (phase19.16, партия SB120,
 * этап 4; db-design-sb120-stage4 §5, uiux-cart §7; протокол —
 * lib/site-view/canvas-cart-messages.ts).
 *
 * Пока открыт экран «Корзина» или раздел «Вид корзины» (`active`), после
 * каждой загрузки окна канвы кабинет шлёт `commora:canvas-cart { open: true,
 * view: "cart" }` и ждёт `canvas-cart-state`. Выбор «Окно на 4 с» в «После
 * «В корзину»» показывает его (`view: "peek"`), другой выбор — снова открытую
 * корзину. Вид и цвета корзины канва получает живой темой — здесь их нет.
 *
 * Ожидание ответа (ревью витрины 2026-10-19):
 *  - успех — только `open: true`;
 *  - `open: false`, пока ждём ответа на свою команду, — команда, скорее
 *    всего, потерялась (окно загрузилось раньше гидрации, а витрина после
 *    монтирования сама шлёт «закрыта»): повторяем один раз через
 *    `CANVAS_CART_RESEND_MS`; снова «закрыта» — `closed`;
 *  - `open: false` после успешного открытия — корзину закрыли на образце
 *    (×, Esc, конец «окна на 4 с»): сразу `closed`, без повтора;
 *  - нет ответа за `CANVAS_CART_REPLY_MS` — `failed`.
 * `closed` — «Корзина на образце закрыта» и «Показать снова», `failed` —
 * «Не удалось показать корзину…» и «Повторить»; обе кнопки — `retry`.
 *
 * Новое окно канвы (`loadKey`) снимает прежнее ожидание: молчание старого
 * окна — не его ошибка.
 */

import type { SiteCartAfterAdd } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import { postToCanvas } from "../../lib/site-view/canvas-messages";
import { CANVAS_CART_REPLY_MS, canvasCartMessage, canvasCartStateOf, canvasCartViewFor, type CanvasCartView } from "../../lib/site-view/canvas-cart-messages";

/** Задержка повтора команды, если канва ответила «закрыта» до открытия. */
export const CANVAS_CART_RESEND_MS = 300;

interface Options {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  /** Корзина нужна на канве: экран «Корзина» или «Вид корзины», черновик без чужого источника. */
  active: boolean;
  /** «После «В корзину»» из живой темы: смена на «Окно на 4 с» показывает его. */
  afterAdd: SiteCartAfterAdd | null;
  /** Ключ окна канвы: новый — окно пересоздано, ждём его загрузки. */
  loadKey: string;
}

export interface CanvasCart {
  /** Окно канвы загрузилось (вызывать из `onLoad` iframe). */
  onLoad: () => void;
  /** Канва не ответила — показать состояние ошибки. */
  failed: boolean;
  /** Корзина на образце закрыта (после повтора или закрыта на образце) — предложить «Показать снова». */
  closed: boolean;
  /** «Повторить» и «Показать снова»: открыть корзину заново (`view: "cart"`). */
  retry: () => void;
}

/** Ожидание ответа на свою команду. */
interface Waiting {
  view: CanvasCartView;
  /** Уже повторили после «закрыта». */
  resent: boolean;
}

export function useCanvasCart({ iframeRef, active, afterAdd, loadKey }: Options): CanvasCart {
  const [failed, setFailed] = useState(false);
  const [closed, setClosed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waiting = useRef<Waiting | null>(null);
  const loaded = useRef(false);
  const latest = useRef({ active, afterAdd });
  latest.current = { active, afterAdd };

  const stop = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    if (resendTimer.current !== null) clearTimeout(resendTimer.current);
    timer.current = null;
    resendTimer.current = null;
    waiting.current = null;
  }, []);

  /** Отправить команду и ждать ответа; `resent` — это повтор после «закрыта». */
  const post = useCallback(
    (view: CanvasCartView, resent: boolean) => {
      if (timer.current !== null) clearTimeout(timer.current);
      waiting.current = { view, resent };
      postToCanvas(iframeRef.current, canvasCartMessage(true, view));
      timer.current = setTimeout(() => {
        timer.current = null;
        waiting.current = null;
        setFailed(true);
      }, CANVAS_CART_REPLY_MS);
    },
    [iframeRef],
  );

  const send = useCallback(
    (view: CanvasCartView) => {
      stop();
      post(view, false);
    },
    [post, stop],
  );

  // Новое окно канвы или корзина больше не нужна — прежнее ожидание снимается.
  useEffect(() => {
    loaded.current = false;
    stop();
    setFailed(false);
    setClosed(false);
  }, [loadKey, stop]);
  useEffect(() => {
    if (active) return;
    stop();
    setFailed(false);
    setClosed(false);
  }, [active, stop]);

  // Сменили «После «В корзину»» — показать на канве (окно на 4 с или снова корзину).
  const shownAfter = useRef(afterAdd);
  useEffect(() => {
    const prev = shownAfter.current;
    shownAfter.current = afterAdd;
    if (prev === afterAdd || afterAdd === null || !active || !loaded.current) return;
    send(canvasCartViewFor(afterAdd));
  }, [afterAdd, active, send]);

  // Ответ канвы — только от своего окна.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const state = canvasCartStateOf(event.data);
      if (!state) return;
      const wait = waiting.current;
      if (state.open) {
        stop();
        setFailed(false);
        setClosed(false);
        return;
      }
      // «Закрыта» до открытия — команда могла потеряться: повторяем один раз.
      if (wait && !wait.resent) {
        waiting.current = { ...wait, resent: true };
        if (resendTimer.current !== null) clearTimeout(resendTimer.current);
        resendTimer.current = setTimeout(() => {
          resendTimer.current = null;
          post(wait.view, true);
        }, CANVAS_CART_RESEND_MS);
        return;
      }
      // Повтор не помог или закрыли после открытия — «Показать снова».
      stop();
      setFailed(false);
      setClosed(true);
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      stop();
    };
  }, [iframeRef, post, stop]);

  const onLoad = useCallback(() => {
    loaded.current = true;
    if (latest.current.active) send("cart");
  }, [send]);

  const retry = useCallback(() => {
    setFailed(false);
    setClosed(false);
    send("cart");
  }, [send]);

  return { onLoad, failed, closed, retry };
}
